"use client";

import { useEffect } from "react";

import { addToCart } from "@/app/actions/cart";
import { getProductsBySkus } from "@/app/actions/product";
import {
	PC_ADD_TO_CART_EVENT,
	isAddToCartEventDetail
} from "@/app/components/shared/recommended-products/add-to-cart-event";
import {
	useCartStore,
	useSessionStore,
	useAddToCartSliderStore
} from "@/app/store";

/**
 * The "event door" into add-to-cart, for surfaces that can't call React
 * directly — external bundles like `consumer-web-elements` (Shadow-DOM web
 * components). They dispatch a `pc:add-to-cart` window event; this single,
 * always-mounted listener runs the SAME canonical `addToCart` action, cart
 * store, and slider that the native `useAddToCart` hook uses. It is not a
 * separate cart — just the bridge for non-React callers.
 *
 * Native React surfaces (PDP button, in-tree composites) skip this and call
 * `useAddToCart` directly. Mounted once in the root layout.
 */
export function CartEventBridge(): null {
	const openSlider = useAddToCartSliderStore((state) => state.open);
	const setCartState = useCartStore((state) => state.setCartState);

	useEffect(() => {
		async function handleAddToCart(event: Event): Promise<void> {
			const detail = (event as CustomEvent).detail;
			if (!isAddToCartEventDetail(detail)) return;

			const sessionState = useSessionStore.getState().sessionState;
			if (!sessionState) return;

			const response = await addToCart(sessionState, {
				productCode: detail.productCode,
				quantity: detail.quantity,
				purchaseType: detail.purchaseType
			});

			// TODO: surface failures via the planned Global Notification.
			if (response.status === "failed") return;

			setCartState(response.cart);
			await openSliderForProduct(detail.productCode, detail.quantity);
		}

		async function openSliderForProduct(
			sku: string,
			quantity: string
		): Promise<void> {
			const result = await getProductsBySkus([sku]);
			const product =
				result.status === "success" ? result.products[0] : undefined;
			if (!product) return;

			openSlider({
				sku: product.sku,
				quantity: Number(quantity) || 1,
				name: product.name,
				imageUrl: product.image,
				listPrice: product.listPrice,
				salePrice: product.salePrice
			});
		}

		window.addEventListener(PC_ADD_TO_CART_EVENT, handleAddToCart);
		return () => {
			window.removeEventListener(PC_ADD_TO_CART_EVENT, handleAddToCart);
		};
	}, [openSlider, setCartState]);

	return null;
}
